<template>
  <div class="customer-display-page">
    <section v-if="!activeSnapshot" class="idle-display">
      <div
        v-for="(slide, index) in slides"
        :key="slide"
        class="idle-display-image"
        :class="{ active: index === currentSlide }"
        :style="{ backgroundImage: `url(${slide})` }"
      ></div>
      <div class="idle-display-overlay"></div>

      <customer-display-header
        :branch-name="branchName"
        :company-initial="companyInitial"
        :company-logo="companyLogo"
      />
    </section>

    <section v-else class="active-display">
      <customer-display-header
        active
        :branch-name="branchName"
        :company-initial="companyInitial"
        :company-logo="companyLogo"
      />

      <main class="active-grid">
        <student-display-panel :snapshot="snapshot" />
        <payment-summary-panel :paid="summaryPaid" :total="summaryTotal" />
        <invoice-items-panel :rows="displayRows" />
        <qr-payment-panel
          :khqr="activeKhqr"
          :name="khqrDisplayName"
          :summary-due="summaryDue"
        />
      </main>
    </section>

    <transition name="payment-success">
      <div v-if="paymentSuccessVisible" class="payment-success-overlay">
        <div class="payment-success-card">
          <div class="payment-success-icon">
            <i class="fas fa-check"></i>
          </div>
          <div class="payment-success-title-kh">{{ $t('customerDisplay.paymentSuccessfulKh') }}</div>
          <div class="payment-success-title">{{ $t('customerDisplay.paymentSuccessful') }}</div>
          <div class="payment-success-detail">
            <div class="payment-success-student">
              <span>{{ $t('customerDisplay.student') }}</span>
              <strong>{{ paymentSuccessName || '-' }}</strong>
            </div>
            <div class="payment-success-received">
              <span>{{ $t('customerDisplay.received') }}</span>
              <strong>{{ paymentSuccessAmount }}</strong>
            </div>
          </div>
          <div class="payment-success-footer">{{ $t('customerDisplay.paymentSuccessMessage') }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import slideOne from '@/assets/img/login-bg/login-bg-1.jpg'
import slideTwo from '@/assets/img/login-bg/login-bg-5.jpg'
import slideThree from '@/assets/img/login-bg/login-bg-7.jpg'
import CustomerDisplayHeader from './components/CustomerDisplayHeader.vue'
import InvoiceItemsPanel from './components/InvoiceItemsPanel.vue'
import PaymentSummaryPanel from './components/PaymentSummaryPanel.vue'
import QrPaymentPanel from './components/QrPaymentPanel.vue'
import StudentDisplayPanel from './components/StudentDisplayPanel.vue'

const SNAPSHOT_KEY = 'pre_invoice_customer_display_snapshot'
const DISPLAY_CHANNEL = 'pre-invoice-customer-display'
const SNAPSHOT_TTL_MS = 30 * 60 * 1000
const PAYMENT_SUCCESS_VISIBLE_MS = 5000

export default {
  name: 'CustomerDisplayInvoice',
  components: {
    CustomerDisplayHeader,
    InvoiceItemsPanel,
    PaymentSummaryPanel,
    QrPaymentPanel,
    StudentDisplayPanel
  },
  data() {
    return {
      snapshot: null,
      timer: null,
      slideTimer: null,
      currentSlide: 0,
      slideDelay: 5000,
      serverSlides: [],
      fallbackSlides: [slideOne, slideTwo, slideThree],
      paymentSuccessVisible: false,
      paymentSuccessTimer: null,
      lastPaymentSuccessKey: null,
      clearedPaymentSuccessKey: null,
      paymentSuccessPreview: null,
      displayChannel: null
    }
  },
  computed: {
    authUser() {
      return this.$store.state.authUser || {}
    },
    company() {
      return this.authUser.company || {}
    },
    companyLogo() {
      const company = this.authUser && this.authUser.company ? this.authUser.company : null
      return company
        ? company.image_logo || company.logo || company.company_logo || company.image_url || ''
        : ''
    },
    companyInitial() {
      const company = this.authUser && this.authUser.company ? this.authUser.company : {}
      const name = company.company_name_en || company.prefix || 'TV'
      return String(name).trim().charAt(0).toUpperCase() || 'T'
    },
    branchName() {
      const branch = this.snapshot && this.snapshot.branch_name ? this.snapshot.branch_name : this.authUser.default_branch
      if (typeof branch === 'string') return branch
      return branch ? (branch.name_kh || branch.branch_name_native || branch.name || branch.branch_name_foreign || '') : ''
    },
    slides() {
      return this.serverSlides.length ? this.serverSlides : this.fallbackSlides
    },
    activeSnapshot() {
      if (!this.snapshot || !this.snapshot.active) return false
      const sentAt = Date.parse(this.snapshot.updated_at || '')
      if (!sentAt) return true
      return Date.now() - sentAt <= SNAPSHOT_TTL_MS
    },
    displayRows() {
      const rows = this.snapshot && (this.snapshot.rows || this.snapshot.items)
      return (rows || []).map(item => ({
        fee_type: item.fee_type || item.fee_type_name || '',
        fee_name: item.fee_name || '',
        qty: item.qty || item.quantity || 0,
        unit_price: item.unit_price || item.amount || 0,
        sub_total: item.sub_total || item.amount || 0
      }))
    },
    summaryTotal() {
      return Number((this.snapshot && this.snapshot.summary && this.snapshot.summary.total) || (this.snapshot && this.snapshot.total_amount) || 0)
    },
    summaryPaid() {
      return Number((this.snapshot && this.snapshot.summary && this.snapshot.summary.paid) || 0)
    },
    summaryDue() {
      return Number((this.snapshot && this.snapshot.summary && this.snapshot.summary.due) || this.summaryTotal)
    },
    displayStudentName() {
      if (!this.snapshot) return ''
      const name = [this.snapshot.last_name_en, this.snapshot.first_name_en].filter(Boolean).join(' ')
      return name || this.snapshot.student_name || ''
    },
    displayStudentNameKh() {
      if (!this.snapshot) return ''
      const name = [this.snapshot.last_name_kh, this.snapshot.first_name_kh].filter(Boolean).join(' ')
      return name || this.snapshot.student_name_kh || ''
    },
    activeKhqr() {
      return this.snapshot && this.snapshot.khqr && this.snapshot.khqr.qr_payload ? this.snapshot.khqr : null
    },
    khqrDisplayName() {
      return this.activeKhqr
        ? this.displayStudentName || this.displayStudentNameKh || '-'
        : ''
    },
    paymentSuccessName() {
      return (this.paymentSuccessPreview && this.paymentSuccessPreview.name) || this.displayStudentName || this.displayStudentNameKh || ''
    },
    paymentSuccessAmount() {
      const khqr = this.snapshot && this.snapshot.khqr ? this.snapshot.khqr : {}
      const preview = this.paymentSuccessPreview || {}
      const amount = Number(preview.amount || khqr.amount || this.summaryPaid || this.summaryTotal || 0)
      const currency = String(preview.currency || khqr.currency_code || 'USD').toUpperCase()
      const fraction = currency === 'KHR' ? 0 : 2
      return `${amount.toLocaleString(undefined, { minimumFractionDigits: fraction, maximumFractionDigits: fraction })} ${currency}`
    }
  },
  watch: {
    snapshot: {
      deep: true,
      handler() {
        this.handlePaymentSuccessState()
      }
    }
  },
  mounted() {
    this.loadSnapshot()
    this.loadFrontCoverImages()
    this.showPaymentSuccessPreview()
    this.registerDisplayChannel()
    window.addEventListener('message', this.onDisplayMessage)
    window.addEventListener('storage', this.onStorage)
    this.timer = window.setInterval(this.loadSnapshot, 1000)
    this.startSlideTimer()
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onDisplayMessage)
    window.removeEventListener('storage', this.onStorage)
    if (this.displayChannel) {
      this.displayChannel.close()
      this.displayChannel = null
    }
    if (this.timer) window.clearInterval(this.timer)
    if (this.slideTimer) window.clearInterval(this.slideTimer)
    if (this.paymentSuccessTimer) window.clearTimeout(this.paymentSuccessTimer)
  },
  methods: {
    startSlideTimer() {
      if (this.slideTimer) window.clearInterval(this.slideTimer)
      this.slideTimer = window.setInterval(() => {
        this.currentSlide = (this.currentSlide + 1) % this.slides.length
      }, this.slideDelay)
    },
    async loadFrontCoverImages() {
      try {
        const response = await this.$store.dispatch('frontCover/fetchFrontCover')
        const images = (response.frontCover || [])
          .map(item => item.image_path)
          .filter(image => typeof image === 'string' && image.trim() !== '')
        if (images.length) {
          this.serverSlides = images
          this.currentSlide = 0
        }
        if (response.interval) {
          const delay = Number(response.interval)
          if (Number.isFinite(delay) && delay > 0) {
            this.slideDelay = delay
            this.startSlideTimer()
          }
        }
      } catch (error) {
        this.serverSlides = []
      }
    },
    registerDisplayChannel() {
      if (typeof window.BroadcastChannel !== 'function') return
      this.displayChannel = new window.BroadcastChannel(DISPLAY_CHANNEL)
      this.displayChannel.onmessage = event => this.applyDisplayMessage(event.data)
    },
    onDisplayMessage(event) {
      if (event.origin !== window.location.origin) return
      this.applyDisplayMessage(event.data)
    },
    applyDisplayMessage(message) {
      if (!message || message.version !== 'v1') return
      if (message.type === 'snapshot' && message.payload) {
        this.applySnapshot(message.payload)
        return
      }
      if (message.type === 'payment_success' && message.payload) {
        this.showPaymentSuccess(this.normalizePaymentSuccessPayload(message.payload))
        return
      }
      if (message.type === 'clear') {
        this.snapshot = null
      }
    },
    onStorage(event) {
      if (event.key === SNAPSHOT_KEY) this.loadSnapshot()
    },
    loadSnapshot() {
      try {
        const raw = window.localStorage.getItem(SNAPSHOT_KEY)
        this.applySnapshot(raw ? JSON.parse(raw) : null)
      } catch (e) {
        this.snapshot = null
      }
    },
    applySnapshot(snapshot) {
      if (!snapshot) {
        this.snapshot = null
        return
      }

      const successKey = this.paymentSuccessKeyFromSnapshot(snapshot)
      if (
        successKey &&
        successKey === this.clearedPaymentSuccessKey &&
        this.isPaidKhqrSnapshot(snapshot)
      ) {
        const clearedSnapshot = this.emptySuccessfulPaymentSnapshot(snapshot)
        this.snapshot = clearedSnapshot
        window.localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(clearedSnapshot))
        return
      }

      this.snapshot = snapshot
      this.handlePaymentSuccessState()
    },
    showPaymentSuccessPreview() {
      const query = this.$route && this.$route.query ? this.$route.query : {}
      if (!query.payment_success_preview) return

      this.paymentSuccessPreview = {
        name: query.name || 'Rabina Botra',
        amount: query.amount || 35,
        currency: query.currency || 'USD'
      }
      this.paymentSuccessVisible = true
      if (this.paymentSuccessTimer) {
        window.clearTimeout(this.paymentSuccessTimer)
        this.paymentSuccessTimer = null
      }
    },
    handlePaymentSuccessState() {
      const khqr = this.snapshot && this.snapshot.khqr ? this.snapshot.khqr : null
      if (!this.isPaidKhqrSnapshot(this.snapshot)) return

      const successKey = this.paymentSuccessKeyFromSnapshot(this.snapshot)
      if (!successKey) return

      this.showPaymentSuccess(this.normalizePaymentSuccessPayload(Object.assign({
        key: successKey,
        name: this.displayStudentName || this.displayStudentNameKh,
        amount: khqr.amount || this.summaryPaid || this.summaryTotal,
        currency: khqr.currency_code || 'USD'
      }, this.snapshot.payment_success || {})))
    },
    normalizePaymentSuccessPayload(payload) {
      const khqr = payload && payload.khqr ? payload.khqr : {}
      const summary = payload && payload.summary ? payload.summary : {}
      const success = payload && payload.payment_success ? payload.payment_success : payload
      const name = success.name || payload.student_name_kh || payload.student_name || ''
      return {
        key: success.key || [khqr.bill_number, khqr.reference, khqr.payment_attempt_id, payload.pre_invoice_number].filter(Boolean).join(':'),
        name,
        amount: Number(success.amount || khqr.amount || summary.paid || summary.total || payload.total_amount || 0),
        currency: success.currency || khqr.currency_code || 'USD'
      }
    },
    showPaymentSuccess(payload) {
      const successKey = payload && payload.key ? payload.key : null
      if (!successKey || successKey === this.lastPaymentSuccessKey) return

      if (successKey !== this.clearedPaymentSuccessKey) {
        this.clearedPaymentSuccessKey = null
      }
      this.lastPaymentSuccessKey = successKey
      this.paymentSuccessPreview = payload
      this.paymentSuccessVisible = true

      if (this.paymentSuccessTimer) window.clearTimeout(this.paymentSuccessTimer)
      this.paymentSuccessTimer = window.setTimeout(() => {
        this.paymentSuccessVisible = false
        this.paymentSuccessTimer = null
        this.clearedPaymentSuccessKey = successKey
        this.clearSuccessfulPaymentSnapshot()
      }, PAYMENT_SUCCESS_VISIBLE_MS)
    },
    clearSuccessfulPaymentSnapshot() {
      if (!this.isPaidKhqrSnapshot(this.snapshot)) return

      const successKey = this.paymentSuccessKeyFromSnapshot(this.snapshot)
      if (successKey) this.clearedPaymentSuccessKey = successKey

      const clearedSnapshot = this.emptySuccessfulPaymentSnapshot(this.snapshot)
      this.snapshot = clearedSnapshot
      window.localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(clearedSnapshot))
    },
    isPaidKhqrSnapshot(snapshot) {
      const khqr = snapshot && snapshot.khqr ? snapshot.khqr : null
      return !!khqr && String(khqr.status || '').toLowerCase() === 'paid'
    },
    paymentSuccessKeyFromSnapshot(snapshot) {
      if (!snapshot) return ''
      const khqr = snapshot.khqr || {}
      const success = snapshot.payment_success || {}
      return success.key || [
        khqr.bill_number,
        khqr.reference,
        khqr.id,
        khqr.payment_attempt_id,
        snapshot.pre_invoice_number
      ].filter(Boolean).join(':')
    },
    emptySuccessfulPaymentSnapshot(snapshot) {
      return Object.assign({}, snapshot, {
        pre_invoice_number: '',
        student_name: '',
        student_name_kh: '',
        first_name_en: '',
        last_name_en: '',
        first_name_kh: '',
        last_name_kh: '',
        card_number: '',
        photo: '',
        phone: '',
        gender: '',
        dob: '',
        grade: '',
        academic: {},
        rows: [],
        items: [],
        summary: {
          subtotal: 0,
          total: 0,
          paid: 0,
          due: 0
        },
        total_amount: 0,
        khqr: null,
        payment_success: null,
        updated_at: new Date().toISOString()
      })
    }
  }
}
</script>

<style>
.customer-display-page {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #edf2f7;
  color: #0f172a;
}

.idle-display,
.active-display {
  width: 100vw;
  height: 100vh;
  padding: 16px;
  background: #e8eef4;
}

.idle-display {
  position: relative;
  overflow: hidden;
}

.active-display {
  overflow: hidden;
}

.idle-display-image {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  opacity: 0;
  transform: scale(1.02);
  transition: opacity 0.7s ease, transform 4.5s ease;
}

.idle-display-image.active {
  opacity: 1;
  transform: scale(1);
}

.idle-display-overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.28), rgba(15, 23, 42, 0.08));
}

.idle-display .display-glass-header {
  position: relative;
  z-index: 2;
}

.display-glass-header,
.active-header {
  height: 80px;
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr) 260px;
  align-items: center;
  gap: 14px;
  border: 1px solid rgba(219, 228, 239, 0.58);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.52);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  backdrop-filter: blur(9px) saturate(1.18);
  -webkit-backdrop-filter: blur(9px) saturate(1.18);
  overflow: hidden;
}

.display-brand {
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 8px 26px;
}

.display-brand img {
  max-width: 300px;
  max-height: 58px;
  width: auto;
  height: auto;
  object-fit: contain;
}

.display-logo-fallback {
  width: 54px;
  height: 54px;
  border-radius: 12px;
  background: #e9f2ff;
  color: #375f96;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 900;
}

.active-branch,
.active-source,
.display-branch,
.display-status {
  min-width: 0;
}

.active-branch,
.display-branch {
  text-align: center;
}

.active-source,
.display-status {
  text-align: right;
  padding-right: 22px;
}

.active-branch strong,
.active-source strong,
.display-branch strong,
.display-status strong {
  display: block;
  color: #0f172a;
  font-size: 20px;
  line-height: 1.2;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.active-branch span,
.active-source span,
.display-branch span,
.display-status span {
  display: block;
  margin-top: 4px;
  color: #60749a;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.active-grid {
  height: calc(100vh - 124px);
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  grid-template-rows: 170px minmax(0, 1fr);
  gap: 12px;
}

.display-panel {
  background: #ffffff;
  border: 1px solid #dbe4ef;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}

.student-panel {
  grid-column: span 8;
  padding: 10px;
  display: grid;
  grid-template-columns: 132px minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  gap: 12px;
}

.student-identity {
  min-width: 0;
  display: contents;
}

.student-avatar {
  width: 132px;
  height: 132px;
  border-radius: 12px;
  background: #f1f5f9;
  color: #8fa2bf;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 54px;
  overflow: hidden;
}

.student-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.student-panel-empty {
  display: flex;
  align-items: center;
  justify-content: center;
}

.student-pending {
  width: 100%;
  height: 100%;
  min-height: 132px;
  border-radius: 12px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #60749a;
  text-align: center;
}

.student-pending-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #ffffff;
  color: #8fa2bf;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  border: 1px solid #dbe4ef;
  box-shadow: 0 3px 8px rgba(15, 23, 42, 0.08);
}

.student-pending strong {
  color: #0f172a;
  font-size: 18px;
  font-weight: 800;
}

.student-pending span {
  max-width: 560px;
  font-size: 14px;
  line-height: 1.4;
}

.student-name-block {
  min-width: 0;
  position: relative;
  padding: 0 110px 0 0;
  align-self: start;
}

.student-code {
  position: absolute;
  top: 2px;
  right: 0;
  max-width: 104px;
  padding: 6px 11px;
  border-radius: 8px;
  background: #eef2f7;
  color: #375f96;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.student-name-block h1,
.student-name-block h2 {
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.student-name-block h1 {
  padding-top: 4px;
  padding-bottom: 2px;
  font-size: 24px;
  line-height: 1.5;
  font-weight: 900;
  color: #0f172a;
}

.student-name-block h2 {
  margin-top: -2px;
  padding-bottom: 1px;
  font-size: 15px;
  line-height: 1.35;
  font-weight: 800;
  color: #1f2a44;
}

.student-info-grid {
  grid-column: 2;
  min-width: 0;
  align-self: end;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 18px;
}

.student-info-item {
  min-width: 0;
  display: grid;
  grid-template-columns: 22px minmax(82px, auto) minmax(0, 1fr);
  align-items: center;
  column-gap: 9px;
}

.student-info-item i {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.info-date i {
  color: #2563eb;
  background: #dbeafe;
}

.info-phone i {
  color: #059669;
  background: #d1fae5;
}

.info-gender i {
  color: #7c3aed;
  background: #ede9fe;
}

.info-grade i {
  color: #1d4ed8;
  background: #dbeafe;
}

.info-dob i {
  color: #f97316;
  background: #ffedd5;
}

.student-info-grid span,
.summary-panel span {
  color: #475569;
  font-size: 13px;
  line-height: 1.2;
  font-weight: 800;
  white-space: nowrap;
}

.student-info-grid strong,
.summary-panel strong {
  min-width: 0;
  color: #020617;
  font-size: 13px;
  line-height: 1.2;
  font-weight: 900;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.summary-panel {
  grid-column: span 4;
  padding: 12px;
  display: grid;
  grid-template-rows: 24px minmax(0, 1fr);
  gap: 8px;
  background: linear-gradient(135deg, #ffffff, #f8fbff);
}

.summary-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #60749a;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.summary-title i {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #dbe4ef;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #60749a;
}

.payment-metrics {
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.payment-card {
  min-width: 0;
  min-height: 0;
  border-radius: 12px;
  border: 1px solid #dbe4ef;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 14px;
}

.payment-card-label {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: #60749a;
}

.payment-card-label i {
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  border-radius: 50%;
  background: #eff6ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.payment-card-label span {
  color: #60749a;
  font-size: 11px;
  text-transform: uppercase;
}

.payment-card strong {
  color: #020617;
  font-size: 24px;
  line-height: 1.05;
  font-weight: 900;
}

.paid-card {
  background: #ecfdf5;
  border-color: #a7f3d0;
}

.paid-card .payment-card-label,
.paid-card .payment-card-label span,
.paid-card strong {
  color: #047857;
}

.paid-card .payment-card-label i {
  background: #d1fae5;
}

.items-panel {
  grid-column: span 8;
  min-height: 0;
  overflow: hidden;
}

.items-panel table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 14px;
}

.items-panel th {
  padding: 11px 10px;
  background: #f8fafc;
  color: #1f2a44;
  font-size: 12px;
  font-weight: 800;
  text-align: left;
  border-bottom: 2px solid #cbd5e1;
}

.items-panel td {
  padding: 10px 10px;
  border-bottom: 1px solid #edf2f7;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.items-panel tr:nth-child(odd) td {
  background: #f8fafc;
}

.items-panel td:nth-child(1),
.items-panel td:nth-child(4),
.items-panel th:nth-child(1),
.items-panel th:nth-child(4) {
  text-align: center;
}

.items-panel td:nth-child(5),
.items-panel td:nth-child(6),
.items-panel th:nth-child(5),
.items-panel th:nth-child(6) {
  text-align: right;
}

.col-no { width: 7%; }
.col-type { width: 20%; }
.col-qty { width: 9%; }
.col-price { width: 15%; }
.col-total { width: 16%; }

.empty-row {
  height: 330px;
  text-align: center !important;
  color: #64748b !important;
}

.items-empty {
  min-height: 330px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #8aa0bf;
}

.items-empty i {
  color: #cbd5e1;
  font-size: 42px;
}

.items-empty strong {
  color: #334155;
  font-size: 16px;
  font-weight: 800;
}

.items-empty span {
  font-size: 14px;
}

.qr-panel {
  grid-column: span 4;
  min-height: 0;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-card {
  flex: 0 0 auto;
  max-width: 260px;
  max-height: 100%;
  border-radius: clamp(12px, 2.2vh, 18px);
  overflow: hidden;
  border: 1px solid #e7e7e7;
  box-shadow: 0 18px 35px rgba(15, 23, 42, 0.12);
}


.qr-header {
  height: 14%;
  min-height: 36px;
  max-height: 60px;
  background: #ffffff;
  overflow: hidden;
}

.qr-header img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: fill;
}

.qr-body {
  height: 86%;
  min-height: 0;
  padding: clamp(6px, 1.15vh, 10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #ffffff;
}

.qr-content {
  flex: 0 0 auto;
  max-width: 100%;
}

.qr-payee {
  width: 100%;
  min-height: clamp(12px, 1.8vh, 18px);
  color: #334155;
  font-size: clamp(10px, 1.65vh, 14px);
  line-height: 1.3;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qr-amount {
  width: 100%;
  margin-top: clamp(2px, 0.55vh, 5px);
  padding-bottom: clamp(5px, 0.9vh, 9px);
  border-bottom: 1px dashed #94a3b8;
  color: #020617;
  font-size: clamp(18px, 3.35vh, 28px);
  line-height: 1.08;
  font-weight: 900;
}

.qr-canvas-wrap {
  flex: 1 1 auto;
  min-height: 0;
  margin: 0;
  padding: clamp(8px, 1.4vh, 14px) 0 clamp(3px, 0.7vh, 8px);
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-generated-wrap {
  box-shadow: none;
}

.qr-placeholder {
  width: min(82%, 24vh);
  max-width: 220px;
  aspect-ratio: 1;
  margin: auto 0;
  border-radius: 10px;
  border: clamp(6px, 1.4vh, 10px) solid #0f172a;
  color: #0f172a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 800;
}

.qr-placeholder img {
  width: clamp(42px, 8vh, 74px);
  height: clamp(42px, 8vh, 74px);
  object-fit: contain;
}

.qr-empty {
  width: 100%;
  height: 100%;
  min-height: 0;
  border: 1px dashed #dbe4ef;
  border-radius: 16px;
  background: #f8fafc;
  color: #60749a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
}

.qr-empty i {
  font-size: clamp(28px, 5.5vh, 44px);
  color: #cbd5e1;
}

.qr-empty strong {
  color: #334155;
  font-size: clamp(16px, 2.6vh, 20px);
  font-weight: 800;
}


.payment-success-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.34);
  backdrop-filter: blur(12px) saturate(0.9);
  -webkit-backdrop-filter: blur(12px) saturate(0.9);
}

.payment-success-card {
  width: min(680px, calc(100vw - 48px));
  border-radius: 28px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.78), rgba(236, 253, 245, 0.56));
  border: 1px solid rgba(255, 255, 255, 0.82);
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.26);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 24px 24px;
  overflow: hidden;
}

.payment-success-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #10b981;
  color: #ffffff;
  font-size: 24px;
  box-shadow: 0 16px 36px rgba(16, 185, 129, 0.34);
}

.payment-success-title-kh {
  margin-top: 22px;
  color: #111827;
  font-size: 25px;
  line-height: 1.55;
  font-weight: 900;
}

.payment-success-title {
  margin-top: 0;
  color: #059669;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 900;
}

.payment-success-detail {
  width: 100%;
  margin-top: 20px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.95);
  background: rgba(255, 255, 255, 0.62);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
  text-align: left;
}

.payment-success-student,
.payment-success-received {
  padding: 14px;
}

.payment-success-student {
  min-height: 78px;
  background: rgba(255, 255, 255, 0.66);
}

.payment-success-received {
  min-height: 94px;
  background: rgba(167, 243, 208, 0.42);
  border-top: 1px solid rgba(203, 213, 225, 0.78);
}

.payment-success-student span,
.payment-success-received span {
  display: block;
  color: #64748b;
  font-size: 11px;
  line-height: 1.2;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.payment-success-student strong {
  display: block;
  max-width: 100%;
  margin-top: 10px;
  color: #0f172a;
  font-size: 18px;
  line-height: 1.35;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.payment-success-received strong {
  display: block;
  margin-top: 16px;
  color: #047857;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 900;
}

.payment-success-footer {
  width: 100%;
  margin-top: 14px;
  border-radius: 14px;
  background: #1f2937;
  color: #ffffff;
  font-size: 15px;
  line-height: 1.35;
  font-weight: 900;
  padding: 13px 18px;
}

.payment-success-enter-active,
.payment-success-leave-active {
  transition: opacity 0.2s ease;
}

.payment-success-enter-active .payment-success-card,
.payment-success-leave-active .payment-success-card {
  transition: transform 0.2s ease;
}

.payment-success-enter,
.payment-success-leave-to {
  opacity: 0;
}

.payment-success-enter .payment-success-card,
.payment-success-leave-to .payment-success-card {
  transform: translateY(8px) scale(0.98);
}

@media (max-width: 760px) {
  .display-glass-header,
  .active-header {
    grid-template-columns: 220px minmax(0, 1fr) 220px;
  }

  .active-grid {
    grid-template-rows: auto minmax(0, 1fr) minmax(300px, 40vh);
  }

  .student-panel,
  .summary-panel,
  .items-panel,
  .qr-panel {
    grid-column: span 12;
  }

  .student-panel {
    grid-template-columns: minmax(0, 1fr);
  }

  .student-info-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .payment-metrics {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
